import React, { useState } from 'react';
import cn from 'classnames';
import styles from './PaymentPanel.module.scss';
import { LoaderIcon } from '@repo/icons';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import CadenceToggle from './CadenceToggle';
import { apiRequest } from '@repo/utils/helpers/api';
import { getStripeTestModeEnabled } from '@views/Settings/panels/Staging/StripeTesting';
import { AccountType } from '../SelectAccountTypePanel';

const SELECTED_ACCOUNT_TYPE_KEY = 'clay_selected_account_type';

export const getStoredSelectedAccountType = (): AccountType => {
  return window.localStorage.getItem(SELECTED_ACCOUNT_TYPE_KEY) as AccountType;
};

const setStoredSelectedAccountType = (selectedAccountType: AccountType) => {
  window.localStorage.setItem(SELECTED_ACCOUNT_TYPE_KEY, selectedAccountType);
};

export default function PaymentForm({
  cadence,
  setCadence,
  planID,
  trialDaysLeft,
  accountType,
  ...props
}) {
  const { intent_id, client_secret } = props.setupIntent || {};
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState<string>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const onPaymentElementReady = () => {
    setReady(true);
  };

  const clearMessage = () => {
    if (message) {
      setMessage(undefined);
    }
  };

  const updateSetupIntent = async () => {
    const useTestMode = getStripeTestModeEnabled();
    return await apiRequest<{}>(
      'POST',
      '/billing/stripe/payment/',
      {
        intent_id,
        plan_id: planID,
        query: useTestMode ? { dev: 1 } : {},
      },
      { apiVersion: 'v2' },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || isLoading) {
      return;
    }

    clearMessage();
    setIsLoading(true);

    try {
      await updateSetupIntent();
    } catch (err) {
      console.error(err);
      setMessage('Something went wrong updating your payment details, please try again');
      setIsLoading(false);
      return;
    }

    setStoredSelectedAccountType(accountType);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setMessage('Please fix your payment details');
      setIsLoading(false);
      return;
    }

    const { error } = await stripe.confirmSetup({
      elements,
      clientSecret: client_secret,
      confirmParams: {
        return_url: `${window.location.origin}/onboarding`,
      },
    });

    if (error) {
      console.error(error);
      setIsLoading(false);
      if (error.code === 'card_declined') {
        setMessage('Your card was declined, please try another payment method');
      } else {
        setMessage('Something went wrong with your payment, please try again');
      }
    }
  };

  return (
    <div className={cn(styles.paymentForm, !ready && styles.hidden)}>
      <CadenceToggle className={styles.cadenceToggle} cadence={cadence} setCadence={setCadence} />
      <form className={styles.form} onSubmit={handleSubmit}>
        <PaymentElement onReady={onPaymentElementReady} onChange={clearMessage} />
        <button
          className={styles.submitBtn}
          type="submit"
          disabled={isLoading || !stripe || !elements}
        >
          {!isLoading ? (
            `Start ${trialDaysLeft} day free trial`
          ) : (
            <>
              Confirming <LoaderIcon />
            </>
          )}
        </button>
      </form>
      <LoaderIcon className={styles.loader} />
      {message && <p className={styles.message}>{message}</p>}
    </div>
  );
}
